// Per-page <head> metadata and the list of routes to pre-render.
// Everything is derived from the data files, so new workshops, events or
// foraging species get their own page, tags and sitemap entry automatically.
import { events, featuredEvent } from './eventsData';
import { pastEvents } from './pastEventsData';
import { offerings } from './offeringsData';
import { intro, species } from './foragingData';

export const SITE_URL = 'https://www.subtleanimal.com';
const SITE_NAME = 'Subtle Animal';
const DEFAULT_IMAGE = '/og-image.jpg';
const DEFAULT_DESCRIPTION = 'Nature connection workshops, foraging walks, immersions and retreats in Sydney, NSW – rewilding and ancestral skills with Subtle Animal.';

const PROVIDER = { '@type': 'Organization', name: SITE_NAME, url: SITE_URL };

function absolute(path) {
  return path.startsWith('http') ? path : SITE_URL + encodeURI(path);
}

// Trim long copy to a search-snippet-sized description: as many whole
// sentences as fit. If even the first sentence is too long, use the
// first page-specific fallback that fits, and only then cut at a word boundary.
function summarise(text, fallbacks = [], max = 155) {
  if (text && text.length <= max) return text;
  const sentences = text.match(/[^.!?]+[.!?]+(\s|$)/g) || [];
  let out = '';
  for (const s of sentences) {
    if ((out + s).trim().length > max) break;
    out += s;
  }
  if (out.trim().length >= 70) return out.trim();
  const fallback = [].concat(fallbacks).find(f => f.length <= max);
  if (fallback) return fallback;
  const cut = text.slice(0, max - 1);
  return cut.slice(0, cut.lastIndexOf(' ')).replace(/[\s,;:–-]+$/, '') + '…';
}

function priceNumber(price) {
  const n = parseFloat(String(price).replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? n : undefined;
}

function findEvent(slug) {
  if (slug === featuredEvent.slug) return featuredEvent;
  return events.find(e => e.slug === slug) || pastEvents.find(e => e.slug === slug);
}

function homeJsonLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: SITE_NAME,
    email: 'thesubtleanimal@gmail.com',
    telephone: '+61417430206',
    url: SITE_URL,
    image: absolute(DEFAULT_IMAGE),
    logo: absolute('/brand-logo-leaf.png'),
    description: DEFAULT_DESCRIPTION,
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Sydney',
      addressRegion: 'NSW',
      addressCountry: 'AU',
    },
    areaServed: 'Sydney, NSW, Australia',
    sameAs: [
      'https://www.instagram.com/subtleanimal/',
      'https://www.facebook.com/profile.php?id=61573240501320',
      'https://maps.app.goo.gl/9E3miZv7LYHn7GsG8',
    ],
  };
}

function offeringJsonLd(o, url) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: o.title,
    description: o.desc,
    url,
    image: absolute(o.heroImg || o.img),
    provider: PROVIDER,
    offers: {
      '@type': 'Offer',
      category: 'Private group booking',
      price: priceNumber(o.pricing.min),
      priceCurrency: 'AUD',
      url,
    },
  };
}

function eventJsonLd(ev, url) {
  if (!ev.endDate) return null;
  const price = ev.releases ? ev.releases[ev.releases.length - 1].price : ev.price;
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: ev.title,
    description: ev.desc,
    url,
    image: absolute(ev.heroImg || ev.img),
    startDate: ev.startDate || ev.endDate,
    endDate: ev.endDate,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: ev.location,
      address: { '@type': 'PostalAddress', streetAddress: ev.location, addressRegion: 'NSW', addressCountry: 'AU' },
    },
    organizer: PROVIDER,
    offers: ev.ticketUrl && {
      '@type': 'Offer',
      url: ev.ticketUrl,
      price: priceNumber(price),
      priceCurrency: 'AUD',
      availability: ev.soldOut ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
    },
  };
}

// Returns { title, description, image, url, type, jsonLd } for a pathname.
export function getPageMeta(pathname) {
  const path = pathname.replace(/\/+$/, '') || '/';
  const url = SITE_URL + (path === '/' ? '/' : path);
  const page = (title, description, image = DEFAULT_IMAGE, extra = {}) => ({
    title, description, image: absolute(image), url, type: 'website', jsonLd: null, ...extra,
  });

  if (path === '/') {
    return page('Subtle Animal – Nature Connection, Foraging & Rewilding Workshops in Sydney', DEFAULT_DESCRIPTION, DEFAULT_IMAGE, { jsonLd: homeJsonLd() });
  }
  if (path === '/community-partnerships') {
    return page('Community Partnerships | Subtle Animal', 'Subtle Animal partners with Randwick City Council on The Saturday Circle: monthly hands-on workshops at the Randwick Sustainability Hub.', '/backgrounds/community-partnerships.jpg');
  }
  if (path === '/past-events') {
    return page('Past Events | Subtle Animal', 'A look back at past Subtle Animal workshops and events in Sydney – foraging walks, plant printing, botanical skincare, nature connection and more.');
  }
  if (path === '/foraging-guide') {
    const img = species.find(s => s.images && s.images.length)?.images[0];
    return page('Foraging Guide – Wild Edible Plants & Seaweeds of South-East Australia | Subtle Animal', summarise(intro.region, 'A field guide to wild edible plants and seaweeds of south-east Australia, from city parks and footpaths to beaches, dunes and rock platforms.'), img ? `/foraging/${img}` : DEFAULT_IMAGE);
  }

  const [, section, slug] = path.split('/');
  if (section === 'workshops') {
    const o = offerings.find(x => x.slug === slug);
    if (o) {
      return page(`${o.title} – Private Group Workshop in Sydney | Subtle Animal`, summarise(o.desc, [`${o.title}: a hands-on private group workshop in Sydney, NSW, for up to ${o.pricing.max} people. Enquire to book with Subtle Animal.`, `${o.title}: a private group workshop in Sydney, NSW. Enquire to book.`]), o.heroImg || o.img, { jsonLd: offeringJsonLd(o, url) });
    }
  }
  if (section === 'events') {
    const ev = findEvent(slug);
    if (ev) {
      const when = ev.date ? ` – ${ev.date.split(' · ')[0]}` : '';
      return page(`${ev.title}${when} | Subtle Animal`, summarise(ev.desc, `${ev.title}${ev.location ? ` at ${ev.location}` : ''}. A Subtle Animal workshop.`), ev.heroImg || ev.img, { jsonLd: eventJsonLd(ev, url) });
    }
  }
  if (section === 'foraging-guide') {
    const sp = species.find(s => s.slug === slug);
    if (sp) {
      const img = sp.images && sp.images.length ? `/foraging/${sp.images[0]}` : DEFAULT_IMAGE;
      return page(`${sp.name} (${sp.scientific}) – Foraging Guide | Subtle Animal`, summarise('', [
        `How to identify, forage and prepare ${sp.name} (${sp.scientific}), with lookalikes, cautions and a recipe. From the Subtle Animal foraging guide.`,
        `How to identify, forage and prepare ${sp.name}, with lookalikes, cautions and a recipe. From the Subtle Animal foraging guide.`,
      ]), img, { type: 'article' });
    }
  }
  return page(SITE_NAME, DEFAULT_DESCRIPTION);
}

// Every URL the site serves, derived from the data files.
export function getAllRoutes() {
  const eventSlugs = [featuredEvent.slug, ...events.map(e => e.slug), ...pastEvents.map(e => e.slug)];
  return [
    '/',
    '/community-partnerships',
    '/past-events',
    '/foraging-guide',
    ...offerings.map(o => `/workshops/${o.slug}`),
    ...[...new Set(eventSlugs)].map(s => `/events/${s}`),
    ...species.map(s => `/foraging-guide/${s.slug}`),
  ];
}
